/* eslint-disable react/prop-types */
import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Input,
  Button,
  IconButton,
  Chip,
  Tooltip,
  Sheet,
} from '@mui/joy';
import Collapse from '@mui/material/Collapse';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Drawer icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import ReceiptIcon from '@mui/icons-material/Receipt';
import GroupIcon from '@mui/icons-material/Group';
import SettingsIcon from '@mui/icons-material/Settings';
import PaymentIcon from '@mui/icons-material/Payment';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SecurityIcon from '@mui/icons-material/Security';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CategoryIcon from '@mui/icons-material/Category';
import InventoryIcon from '@mui/icons-material/Inventory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TimelineIcon from '@mui/icons-material/Timeline';
import ListAltIcon from '@mui/icons-material/ListAlt';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import HowToRegIcon from '@mui/icons-material/HowToReg';

import NativeCheckbox from '../../../main/components/NativeCheckbox';
import { PERMISSIONS } from '../../../main/context/AuthContext';

const P = PERMISSIONS;

// ---------------------------------------------------------------------------
// Permission codename → action-label i18n key.
// This turns an opaque codename like "shipments_bulk_delete" into a short,
// human label ("Bulk delete") that tells the admin *which UI affordance*
// the permission gates. Longer suffixes come first so that compound
// suffixes (e.g. "_bulk_update") win over their shorter cousins ("_update").
// ---------------------------------------------------------------------------
const ACTION_SUFFIX_KEYS = [
  ['_bulk_status_update',         'settingsPages.rolesPermsEditor.actions.bulkStatus'],
  ['_bulk_update',                'settingsPages.rolesPermsEditor.actions.bulkUpdate'],
  ['_bulk_delete',                'settingsPages.rolesPermsEditor.actions.bulkDelete'],
  ['_bulk_tags',                  'settingsPages.rolesPermsEditor.actions.bulkTags'],
  ['_bulk_send_shipments',        'settingsPages.rolesPermsEditor.actions.bulkWhatsapp'],
  ['_create_bulk',                'settingsPages.rolesPermsEditor.actions.createBulk'],
  ['_scan_update',                'settingsPages.rolesPermsEditor.actions.scan'],
  ['_upload_excel',               'settingsPages.rolesPermsEditor.actions.importExcel'],
  ['_import',                     'settingsPages.rolesPermsEditor.actions.importExcel'],
  ['_recalculate_price',          'settingsPages.rolesPermsEditor.actions.recalcPrice'],
  ['_deleted_list',               'settingsPages.rolesPermsEditor.actions.deletedTab'],
  ['_recover',                    'settingsPages.rolesPermsEditor.actions.recover'],
  ['_receive',                    'settingsPages.rolesPermsEditor.actions.receive'],
  ['_approve',                    'settingsPages.rolesPermsEditor.actions.approve'],
  ['_assign_permissions',         'settingsPages.rolesPermsEditor.actions.assignPerms'],
  ['_view_permissions',           'settingsPages.rolesPermsEditor.actions.viewPerms'],
  ['_assign_shipments',           'settingsPages.rolesPermsEditor.actions.assignShipments'],
  ['_generate_for_shipments',     'settingsPages.rolesPermsEditor.actions.generateInvoices'],
  ['_regenerate',                 'settingsPages.rolesPermsEditor.actions.regenerate'],
  ['_update_payment_status',      'settingsPages.rolesPermsEditor.actions.updatePayStatus'],
  ['_add_shipment',               'settingsPages.rolesPermsEditor.actions.payShipment'],
  ['_add_invoice',                'settingsPages.rolesPermsEditor.actions.payInvoice'],
  ['_confirm',                    'settingsPages.rolesPermsEditor.actions.confirmPay'],
  ['_refund',                     'settingsPages.rolesPermsEditor.actions.refund'],
  ['_webhook_thawani',            'settingsPages.rolesPermsEditor.actions.webhook'],
  ['_webhook_paymob',             'settingsPages.rolesPermsEditor.actions.webhook'],
  ['_send_shipment_notification', 'settingsPages.rolesPermsEditor.actions.sendWhatsapp'],
  ['_send_shipment_test',         'settingsPages.rolesPermsEditor.actions.sendTest'],
  ['_receive_reply',              'settingsPages.rolesPermsEditor.actions.receiveReply'],
  ['_view_analytics',             'settingsPages.rolesPermsEditor.actions.viewAnalytics'],
  ['_update_site',                'settingsPages.rolesPermsEditor.actions.saveSite'],
  ['_update_detail',              'settingsPages.rolesPermsEditor.actions.saveDetail'],
  ['_view_detail',                'settingsPages.rolesPermsEditor.actions.viewDetail'],
  ['_view_public',                'settingsPages.rolesPermsEditor.actions.viewPublic'],
  ['_files_upload',               'settingsPages.rolesPermsEditor.actions.uploadFile'],
  ['_files_update',               'settingsPages.rolesPermsEditor.actions.editFile'],
  ['_files_delete',               'settingsPages.rolesPermsEditor.actions.deleteFile'],
  ['_files_list',                 'settingsPages.rolesPermsEditor.actions.listFiles'],
  ['_files_view',                 'settingsPages.rolesPermsEditor.actions.viewFile'],
  ['_role_assign',                'settingsPages.rolesPermsEditor.actions.assignRole'],
  ['_role_revoke',                'settingsPages.rolesPermsEditor.actions.revokeRole'],
  ['_applied_rules_manage',       'settingsPages.rolesPermsEditor.actions.shipmentAppliedRules'],
  ['_scan_lookup',                'settingsPages.rolesPermsEditor.actions.scanLookup'],
  ['_bulk_scan_update',           'settingsPages.rolesPermsEditor.actions.bulkScan'],
  ['_label_batch',                'settingsPages.rolesPermsEditor.actions.printLabelBatch'],
  ['_label',                      'settingsPages.rolesPermsEditor.actions.printLabel'],
  ['_customer_view',              'settingsPages.rolesPermsEditor.actions.view'],
  ['_customer_list_for_shipment', 'settingsPages.rolesPermsEditor.actions.pieceList'],
  ['_manage',                     'settingsPages.rolesPermsEditor.actions.manage'],
  ['_replace',                    'settingsPages.rolesPermsEditor.actions.replace'],
  ['_remove',                     'settingsPages.rolesPermsEditor.actions.remove'],
  ['_assign',                     'settingsPages.rolesPermsEditor.actions.assign'],
  ['_revoke',                     'settingsPages.rolesPermsEditor.actions.revoke'],
  ['_verify',                     'settingsPages.rolesPermsEditor.actions.verify'],
  ['_login',                      'settingsPages.rolesPermsEditor.actions.login'],
  ['_logout',                     'settingsPages.rolesPermsEditor.actions.logout'],
  ['_list',                       'settingsPages.rolesPermsEditor.actions.list'],
  ['_view',                       'settingsPages.rolesPermsEditor.actions.view'],
  ['_create',                     'settingsPages.rolesPermsEditor.actions.create'],
  ['_update',                     'settingsPages.rolesPermsEditor.actions.update'],
  ['_delete',                     'settingsPages.rolesPermsEditor.actions.delete'],
];

/**
 * Returns the i18n key that describes *which UI affordance* a permission
 * gates (e.g. "Bulk delete toolbar", "Regenerate invoice"). Returns null
 * for permissions whose codename we don't recognise.
 */
export const autoActionKey = (code = '') => {
  if (!code) return null;
  const lower = String(code).toLowerCase();
  // #41 in ENDPOINTS_USAGE.md: distinct from #47 (bulk status) and from row "Edit".
  if (lower === 'shipments_bulk_update') {
    return 'settingsPages.rolesPermsEditor.actions.shipmentsBulkUpdateLegacy';
  }
  for (const [suffix, key] of ACTION_SUFFIX_KEYS) {
    if (lower.endsWith(suffix)) return key;
  }
  return null;
};

// ---------------------------------------------------------------------------
// Navigation → Pages → Permissions mapping.
// Source: ENDPOINTS_USAGE.md (Phase 1 route table + Phase 4 domain tables) and
// App.js route modules + AppBar navigation drawer. Each page lists permissions
// consumed on that screen (route + actions, bulk actions, and option-list sources).
// Page order mirrors the main menu: operations → scanning → tracking → inventory.
// ---------------------------------------------------------------------------
export const buildNavigationTree = () => [
  {
    id: 'dashboard',
    labelKey: 'nav.dashboard',
    icon: DashboardIcon,
    pages: [
      {
        id: 'dashboard',
        labelKey: 'nav.dashboard',
        route: '/dashboard',
        icon: DashboardIcon,
        permissions: [
          { code: P.DASHBOARD_VIEW_ANALYTICS, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
        ],
      },
    ],
  },
  {
    id: 'shipments',
    labelKey: 'nav.sectionShipments',
    icon: LocalShippingIcon,
    pages: [
      {
        id: 'shipmentsList',
        labelKey: 'nav.listShipments',
        route: '/Shipments',
        icon: LocalShippingIcon,
        // ENDPOINTS_USAGE.md — #47 bulk status (UI) uses shipments_bulk_status_update; #41 is legacy API.
        permissions: [
          { code: P.SHIPMENTS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPMENTS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.SHIPMENTS_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.SHIPMENTS_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
          {
            code: P.SHIPMENTS_BULK_UPDATE,
            actionKey: 'settingsPages.rolesPermsEditor.actions.shipmentsBulkUpdateLegacy',
          },
          { code: P.SHIPMENTS_BULK_STATUS_UPDATE },
          { code: P.SHIPMENTS_BULK_DELETE },
          { code: P.SHIPMENTS_BULK_TAGS },
          { code: P.SHIPMENT_TAGS_CREATE },
          { code: P.SHIPMENT_TAGS_UPDATE },
          { code: P.SHIPMENT_TAGS_DELETE },
          { code: P.SHIPMENTS_DELETED_LIST },
          { code: P.SHIPMENTS_RECOVER, actionKey: 'settingsPages.rolesPermsEditor.actions.recoverFromDeleted' },
          { code: P.SHIPMENTS_RECEIVE, actionKey: 'settingsPages.rolesPermsEditor.actions.receiveShipment' },
          { code: P.SHIPMENTS_RECALCULATE_PRICE },
          { code: P.PAYMENTS_ADD_SHIPMENT, actionKey: 'settingsPages.rolesPermsEditor.actions.payShipmentToolbar' },
          { code: P.SETTLEMENT_GROUPS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.paySettlementGroups' },
          { code: P.INVOICES_GENERATE_FOR_SHIPMENTS, actionKey: 'settingsPages.rolesPermsEditor.actions.generateInvoicesToolbar' },
          { code: P.WHATSAPP_BULK_SEND_SHIPMENTS },
          { code: P.SHIPMENT_UNITS_ASSIGN_SHIPMENTS, actionKey: 'settingsPages.rolesPermsEditor.actions.addToUnit' },
          { code: P.SHIPMENT_TYPES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.typeFilter' },
        ],
      },
      {
        id: 'addShipment',
        labelKey: 'nav.addShipment',
        route: '/Shipment/AddShipment',
        icon: AddCircleIcon,
        permissions: [
          { code: P.SHIPMENTS_CREATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPMENT_TYPES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.typeDropdown' },
          { code: P.SHIPMENT_TAGS_CREATE },
          { code: P.SHIPMENT_TAGS_UPDATE },
          { code: P.SHIPMENT_TAGS_DELETE },
          { code: P.WHATSAPP_SEND_SHIPMENT_NOTIFICATION, actionKey: 'settingsPages.rolesPermsEditor.actions.notifyReceiverToggle' },
        ],
      },
      {
        id: 'importShipments',
        labelKey: 'nav.importShipments',
        route: '/Shipment/Import',
        icon: FileUploadIcon,
        permissions: [
          { code: P.SHIPMENTS_UPLOAD_EXCEL, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
        ],
      },
      {
        id: 'scanPiece',
        labelKey: 'nav.scanPiece',
        route: '/shipment/pieces/scan',
        icon: QrCodeScannerIcon,
        permissions: [
          { code: P.SHIPMENT_PIECES_SCAN_UPDATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPMENT_PIECES_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.loadShipment' },
          { code: P.SHIPMENT_PIECES_SCAN_LOOKUP },
          { code: P.SHIPMENT_PIECES_BULK_SCAN_UPDATE },
          { code: P.SHIPMENT_PIECES_LABEL, actionKey: 'settingsPages.rolesPermsEditor.actions.printLabel' },
          { code: P.SHIPMENT_PIECES_LABEL_BATCH, actionKey: 'settingsPages.rolesPermsEditor.actions.printLabelBatch' },
          { code: P.SHIPMENT_TRACKING_EVENT_TYPES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.eventTypeDropdown' },
          { code: P.LOCATIONS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.locationDropdown' },
          { code: P.WHATSAPP_SEND_SHIPMENT_NOTIFICATION, actionKey: 'settingsPages.rolesPermsEditor.actions.sendWhatsapp' },
          { code: P.SCAN_RESOLVE, actionKey: 'settingsPages.rolesPermsEditor.actions.scanResolve' },
        ],
      },
      {
        id: 'scanShipment',
        labelKey: 'nav.scanShipments',
        route: '/Shipment/Scan',
        icon: QrCodeScannerIcon,
        permissions: [
          { code: P.SHIPMENTS_SCAN_UPDATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPMENTS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.loadShipment' },
          { code: P.CUSTOMERS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.customerLookup' },
          { code: P.SHIPMENTS_RECEIVE, actionKey: 'settingsPages.rolesPermsEditor.actions.receiveScan' },
          { code: P.PAYMENTS_ADD_SHIPMENT, actionKey: 'settingsPages.rolesPermsEditor.actions.codPay' },
          { code: P.CURRENT_LOCATION_MANAGE },
          { code: P.SHIPMENT_TRACKING_EVENT_TYPES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.eventTypeDropdown' },
          { code: P.LOCATIONS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.locationDropdown' },
          { code: P.SHIPMENT_TAGS_CREATE },
          { code: P.SHIPMENT_TAGS_UPDATE },
          { code: P.SHIPMENT_TAGS_DELETE },
          { code: P.SCAN_RESOLVE, actionKey: 'settingsPages.rolesPermsEditor.actions.scanResolve' },
          { code: P.SHIPMENT_UNIT_SCAN_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.scanUnit' },
        ],
      },
      {
        id: 'universalScanner',
        labelKey: 'nav.universalScanner',
        route: '/scan',
        icon: QrCodeScannerIcon,
        permissions: [
          { code: P.SCAN_RESOLVE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPMENT_PIECES_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.view' },
          { code: P.SHIPMENT_UNITS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.view' },
          { code: P.SHIPMENTS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.view' },
          { code: P.SHIPMENT_PIECES_SCAN_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.scan' },
          { code: P.SHIPMENT_UNIT_SCAN_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.scanUnit' },
          { code: P.SHIPMENTS_SCAN_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.scan' },
        ],
      },
      {
        id: 'pieceTrackingEvents',
        labelKey: 'settingsPages.rolesPermsEditor.pieceTrackingEvents',
        route: '/shipment/piece-tracking-events',
        icon: TimelineIcon,
        permissions: [
          { code: P.SHIPMENT_PIECE_TRACKING_EVENTS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPMENT_PIECE_TRACKING_EVENTS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.SHIPMENT_PIECE_TRACKING_EVENTS_CREATE },
          { code: P.SHIPMENT_PIECE_TRACKING_EVENTS_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.SHIPMENT_PIECE_TRACKING_EVENTS_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
          { code: P.SHIPMENT_PIECE_TRACKING_EVENTS_BULK_UPDATE },
          { code: P.SHIPMENT_PIECE_TRACKING_EVENTS_BULK_DELETE },
        ],
      },
      {
        id: 'trackingEvents',
        labelKey: 'nav.shipmentTrackingEvent',
        route: '/shipment/tracking-events',
        icon: TimelineIcon,
        // CREATE is on /add; see §4.7 + Phase 1
        permissions: [
          { code: P.TRACKING_EVENTS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.TRACKING_EVENTS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.TRACKING_EVENTS_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.TRACKING_EVENTS_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
          { code: P.TRACKING_EVENTS_BULK_UPDATE },
          { code: P.TRACKING_EVENTS_BULK_DELETE },
        ],
      },
      {
        id: 'unitTrackingEvents',
        labelKey: 'nav.unitTrackingEvents',
        route: '/shipment/unit-tracking-events',
        icon: TimelineIcon,
        permissions: [
          { code: P.SHIPMENT_UNIT_TRACKING_EVENTS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPMENT_UNITS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.unitFilter' },
          { code: P.SHIPMENT_UNIT_TRACKING_EVENTS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.SHIPMENT_UNIT_TRACKING_EVENTS_CREATE },
          { code: P.SHIPMENT_UNIT_TRACKING_EVENTS_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.SHIPMENT_UNIT_TRACKING_EVENTS_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
          { code: P.SHIPMENT_UNIT_TRACKING_EVENTS_BULK_UPDATE },
          { code: P.SHIPMENT_UNIT_TRACKING_EVENTS_BULK_DELETE },
        ],
      },
      {
        id: 'shipmentPieces',
        labelKey: 'nav.shipmentPieces',
        route: '/shipment/pieces',
        icon: InventoryIcon,
        permissions: [
          { code: P.SHIPMENT_PIECES_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPMENT_PIECES_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.SHIPMENT_PIECES_CREATE },
          { code: P.SHIPMENT_PIECES_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.SHIPMENT_PIECES_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
          { code: P.SHIPMENT_PIECES_RECOVER, actionKey: 'settingsPages.rolesPermsEditor.actions.recover' },
          { code: P.SHIPMENT_PIECES_LABEL, actionKey: 'settingsPages.rolesPermsEditor.actions.printLabel' },
          { code: P.SHIPMENT_PIECES_LABEL_BATCH, actionKey: 'settingsPages.rolesPermsEditor.actions.printLabelBatch' },
          { code: P.SHIPMENT_PIECES_CUSTOMER_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.view' },
          { code: P.SHIPMENT_PIECES_CUSTOMER_LIST_FOR_SHIPMENT, actionKey: 'settingsPages.rolesPermsEditor.actions.pieceList' },
        ],
      },
      {
        id: 'receiverDetails',
        labelKey: 'nav.receiverDetails',
        route: '/shipment/receiver-details',
        icon: PersonIcon,
        permissions: [
          { code: P.RECEIVER_DETAILS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.RECEIVER_DETAILS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.RECEIVER_DETAILS_CREATE },
          { code: P.RECEIVER_DETAILS_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.RECEIVER_DETAILS_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
        ],
      },
      {
        id: 'shipmentUnits',
        labelKey: 'nav.shipmentUnits',
        route: '/shipment/units',
        icon: InventoryIcon,
        permissions: [
          { code: P.SHIPMENT_UNITS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPMENT_UNITS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.SHIPMENT_UNITS_CREATE },
          { code: P.SHIPMENT_UNITS_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.SHIPMENT_UNITS_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
          { code: P.SHIPMENT_UNITS_ASSIGN_SHIPMENTS, actionKey: 'settingsPages.rolesPermsEditor.actions.manageUnitContents' },
          { code: P.SHIPMENTS_RECALCULATE_PRICE, actionKey: 'settingsPages.rolesPermsEditor.actions.recalcPrice' },
          { code: P.PRICING_RULES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.unitPricingRulesList' },
          { code: P.PRICING_RULES_CREATE, actionKey: 'settingsPages.rolesPermsEditor.actions.unitPricingRulesCreate' },
          { code: P.PRICING_RULES_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.unitPricingRulesEdit' },
          { code: P.SHIPMENT_UNIT_SCAN_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.scanUnit' },
          { code: P.SHIPPING_COMPANIES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.shippingCoDropdown' },
          { code: P.SHIPPING_WAREHOUSES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.warehouseDropdown' },
          { code: P.WHATSAPP_SEND_SHIPMENT_NOTIFICATION, actionKey: 'settingsPages.rolesPermsEditor.actions.notifyReceiverAction' },
        ],
      },
      {
        id: 'editShipment',
        labelKey: 'settingsPages.rolesPermsEditor.editShipment',
        route: '/Shipment/:id/EditShipment',
        icon: AddCircleIcon,
        permissions: [
          { code: P.SHIPMENTS_UPDATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPMENTS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.loadShipment' },
          { code: P.SHIPMENT_TYPES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.typeDropdown' },
          { code: P.SHIPMENT_TAGS_CREATE },
          { code: P.SHIPMENT_TAGS_UPDATE },
          { code: P.SHIPMENT_TAGS_DELETE },
          { code: P.SHIPMENTS_APPLIED_RULES_MANAGE, actionKey: 'settingsPages.rolesPermsEditor.actions.shipmentAppliedRules' },
          { code: P.SHIPMENTS_RECALCULATE_PRICE, actionKey: 'settingsPages.rolesPermsEditor.actions.recalcPrice' },
          { code: P.SHIPMENT_PIECES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.piecesPanel' },
          { code: P.SHIPMENT_PIECES_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.view' },
          { code: P.SHIPMENT_PIECES_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.SHIPMENT_PIECES_LABEL, actionKey: 'settingsPages.rolesPermsEditor.actions.printLabel' },
          { code: P.SHIPMENT_PIECES_LABEL_BATCH, actionKey: 'settingsPages.rolesPermsEditor.actions.printLabelBatch' },
        ],
      },
      {
        id: 'pendingShipmentForm',
        labelKey: 'settingsPages.rolesPermsEditor.pendingShipmentForm',
        route: '/Shipment/PendingShipmentForm',
        icon: AddCircleIcon,
        // Phase 1 table: route permission is SHIPMENTS_UPDATE (not create)
        permissions: [
          { code: P.SHIPMENTS_UPDATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPMENTS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.loadShipment' },
          { code: P.SHIPMENT_TYPES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.typeDropdown' },
          { code: P.SHIPMENT_TAGS_CREATE },
          { code: P.SHIPMENT_TAGS_UPDATE },
          { code: P.SHIPMENT_TAGS_DELETE },
        ],
      },
      {
        id: 'trackingEventsAdd',
        labelKey: 'settingsPages.rolesPermsEditor.addTrackingEvent',
        route: '/shipment/tracking-events/add',
        icon: AddCircleIcon,
        permissions: [
          { code: P.TRACKING_EVENTS_CREATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.TRACKING_EVENTS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.listNav' },
          { code: P.SHIPMENT_TRACKING_EVENT_TYPES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.eventTypeDropdown' },
        ],
      },
      {
        id: 'trackingEventsEdit',
        labelKey: 'settingsPages.rolesPermsEditor.editTrackingEvent',
        route: '/shipment/tracking-events/:id/edit',
        icon: AddCircleIcon,
        permissions: [
          { code: P.TRACKING_EVENTS_UPDATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.TRACKING_EVENTS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.loadEvent' },
          { code: P.TRACKING_EVENTS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.listNav' },
          { code: P.SHIPMENT_TRACKING_EVENT_TYPES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.eventTypeDropdown' },
        ],
      },
    ],
  },
  {
    id: 'pricingPlans',
    labelKey: 'nav.pricingPlans',
    icon: AttachMoneyIcon,
    pages: [
      {
        id: 'pricingPlansList',
        labelKey: 'nav.pricingPlans',
        route: '/PricingPlans',
        icon: AttachMoneyIcon,
        permissions: [
          { code: P.PRICING_PLANS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.PRICING_PLANS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.PRICING_PLANS_CREATE },
          { code: P.PRICING_PLANS_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.PRICING_PLANS_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
        ],
      },
      {
        id: 'addPricingPlan',
        labelKey: 'settingsPages.rolesPermsEditor.addPricingPlan',
        route: '/PricingPlans/Add',
        icon: AddCircleIcon,
        permissions: [
          { code: P.PRICING_PLANS_CREATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.PRICING_PLANS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.listNav' },
        ],
      },
      {
        id: 'editPricingPlan',
        labelKey: 'settingsPages.rolesPermsEditor.editPricingPlan',
        route: '/PricingPlans/:id',
        icon: AddCircleIcon,
        permissions: [
          { code: P.PRICING_PLANS_UPDATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.PRICING_PLANS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.loadPricingPlan' },
          { code: P.PRICING_PLANS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.listNav' },
          { code: P.PRICING_RULES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.planBandsList' },
          { code: P.PRICING_RULES_CREATE, actionKey: 'settingsPages.rolesPermsEditor.actions.planBandsCreate' },
        ],
      },
    ],
  },
  {
    id: 'pricingRules',
    labelKey: 'nav.pricingRules',
    icon: MiscellaneousServicesIcon,
    pages: [
      {
        id: 'pricingRulesList',
        labelKey: 'nav.pricingRules',
        route: '/PricingRules',
        icon: MiscellaneousServicesIcon,
        permissions: [
          { code: P.PRICING_RULES_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.PRICING_RULES_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.PRICING_RULES_CREATE },
          { code: P.PRICING_RULES_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.PRICING_RULES_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
          { code: P.PRICING_RULES_IMPORT, actionKey: 'settingsPages.rolesPermsEditor.actions.importExcel' },
        ],
      },
      {
        id: 'importPricingRules',
        labelKey: 'nav.importPricingRules',
        route: '/PricingRules/Import',
        icon: FileUploadIcon,
        permissions: [
          { code: P.PRICING_RULES_IMPORT, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.PRICING_RULES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.listNav' },
        ],
      },
      {
        id: 'addPricingRule',
        labelKey: 'settingsPages.rolesPermsEditor.addPricingRule',
        route: '/PricingRules/Add',
        icon: AddCircleIcon,
        permissions: [
          { code: P.PRICING_RULES_CREATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.PRICING_RULES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.listNav' },
          { code: P.PRICING_PLANS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.planDropdown' },
          { code: P.SETTLEMENT_GROUPS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.settlementGroupDropdown' },
        ],
      },
      {
        id: 'editPricingRule',
        labelKey: 'settingsPages.rolesPermsEditor.editPricingRule',
        route: '/PricingRules/:id/Edit',
        icon: AddCircleIcon,
        permissions: [
          { code: P.PRICING_RULES_UPDATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.PRICING_RULES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.listNav' },
          { code: P.PRICING_RULES_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.loadPricingRule' },
          { code: P.PRICING_PLANS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.planDropdown' },
          { code: P.SETTLEMENT_GROUPS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.settlementGroupDropdown' },
        ],
      },
    ],
  },
  {
    id: 'invoices',
    labelKey: 'nav.invoices',
    icon: ReceiptIcon,
    pages: [
      {
        id: 'invoices',
        labelKey: 'nav.invoices',
        route: '/Invoices',
        icon: ReceiptIcon,
        // §4.21 #137 — generate for shipments is Shipment Manager only, not /Invoices
        permissions: [
          { code: P.INVOICES_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.INVOICES_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.INVOICES_CREATE },
          { code: P.INVOICES_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.INVOICES_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
          { code: P.INVOICES_CREATE_BULK },
          { code: P.INVOICES_REGENERATE },
          { code: P.INVOICES_UPDATE_PAYMENT_STATUS },
          { code: P.WHATSAPP_BULK_SEND_INVOICES, actionKey: 'settingsPages.rolesPermsEditor.actions.sendWhatsapp' },
          { code: P.PAYMENTS_ADD_INVOICE, actionKey: 'settingsPages.rolesPermsEditor.actions.payInvoiceToolbar' },
          { code: P.SETTLEMENT_GROUPS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.paySettlementGroups' },
        ],
      },
      {
        id: 'addInvoice',
        labelKey: 'settingsPages.rolesPermsEditor.addInvoice',
        route: '/Invoices/AddInvoice',
        icon: AddCircleIcon,
        permissions: [
          { code: P.INVOICES_CREATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPMENTS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.invoiceableShipments' },
        ],
      },
      {
        id: 'editInvoice',
        labelKey: 'settingsPages.rolesPermsEditor.editInvoice',
        route: '/Invoices/:id/EditInvoice',
        icon: AddCircleIcon,
        permissions: [
          { code: P.INVOICES_UPDATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.INVOICES_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.loadInvoice' },
          { code: P.INVOICES_REGENERATE },
          { code: P.WHATSAPP_SEND_INVOICE_NOTIFICATION, actionKey: 'settingsPages.rolesPermsEditor.actions.sendWhatsapp' },
          { code: P.SHIPMENTS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.invoiceableShipments' },
        ],
      },
    ],
  },
  {
    id: 'payments',
    labelKey: 'nav.payments',
    icon: PaymentIcon,
    pages: [
      {
        id: 'payments',
        labelKey: 'nav.payments',
        route: '/payments',
        icon: PaymentIcon,
        permissions: [
          { code: P.PAYMENTS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.PAYMENTS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.PAYMENTS_CREATE },
          { code: P.PAYMENTS_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.PAYMENTS_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
          { code: P.PAYMENTS_CONFIRM },
          { code: P.PAYMENTS_REFUND },
          { code: P.PAYMENTS_ADD_SHIPMENT },
          { code: P.PAYMENTS_ADD_INVOICE },
          { code: P.ACCOUNTS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.accountSelector' },
        ],
      },
    ],
  },
  {
    id: 'customers',
    labelKey: 'nav.sectionCustomers',
    icon: GroupIcon,
    pages: [
      {
        id: 'customers',
        labelKey: 'nav.listCustomers',
        route: '/Customers',
        icon: GroupIcon,
        permissions: [
          { code: P.CUSTOMERS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.CUSTOMERS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.CUSTOMERS_CREATE },
          { code: P.CUSTOMERS_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.CUSTOMERS_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
        ],
      },
      {
        id: 'addCustomer',
        labelKey: 'settingsPages.rolesPermsEditor.addCustomer',
        route: '/Customers/AddCustomer',
        icon: AddCircleIcon,
        permissions: [
          { code: P.CUSTOMERS_CREATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.CUSTOMERS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.customersOptionList' },
        ],
      },
      {
        id: 'editCustomer',
        labelKey: 'settingsPages.rolesPermsEditor.editCustomer',
        route: '/Customers/:id/EditCustomer',
        icon: AddCircleIcon,
        permissions: [
          { code: P.CUSTOMERS_UPDATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.CUSTOMERS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.loadCustomer' },
          { code: P.CUSTOMERS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.customersOptionList' },
        ],
      },
      {
        id: 'customerApprovals',
        labelKey: 'nav.customerRegistrationApprovals',
        route: '/customers/pending-registrations',
        icon: HowToRegIcon,
        permissions: [
          { code: P.CUSTOMERS_APPROVE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
        ],
      },
    ],
  },
  {
    id: 'settingsSite',
    labelKey: 'nav.sectionSiteConfig',
    icon: SettingsIcon,
    pages: [
      {
        id: 'settingsHub',
        labelKey: 'settingsPages.rolesPermsEditor.settingsHub',
        route: '/settings',
        icon: SettingsIcon,
        // §4.22 — private settings index: opens page with settings_list; public branding with view_public
        permissions: [
          { code: P.SETTINGS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SETTINGS_VIEW_PUBLIC, actionKey: 'settingsPages.rolesPermsEditor.actions.viewPublic' },
          { code: P.SETTINGS_VIEW_DETAIL, actionKey: 'settingsPages.rolesPermsEditor.actions.viewDetail' },
          { code: P.SETTINGS_UPDATE_SITE },
          { code: P.SETTINGS_UPDATE_DETAIL },
          { code: 'settings_manage' },
          { code: P.SETTINGS_FILES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.fileGallery' },
          { code: P.SETTINGS_FILES_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.viewFile' },
          { code: P.SETTINGS_FILES_UPLOAD, actionKey: 'settingsPages.rolesPermsEditor.actions.uploadBranding' },
          { code: P.SETTINGS_FILES_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editFile' },
          { code: P.SETTINGS_FILES_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteFile' },
        ],
      },
      {
        id: 'generalSettings',
        labelKey: 'nav.generalSettings',
        route: '/settings/general',
        icon: SettingsIcon,
        permissions: [
          { code: P.SETTINGS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SETTINGS_VIEW_PUBLIC, actionKey: 'settingsPages.rolesPermsEditor.actions.viewPublic' },
          { code: P.SETTINGS_VIEW_DETAIL, actionKey: 'settingsPages.rolesPermsEditor.actions.viewDetail' },
          { code: P.SETTINGS_UPDATE_SITE },
          { code: P.SETTINGS_FILES_UPLOAD, actionKey: 'settingsPages.rolesPermsEditor.actions.uploadBranding' },
          { code: 'settings_manage' },
        ],
      },
      {
        id: 'paymentSettings',
        labelKey: 'nav.paymentSettings',
        route: '/settings/payment',
        icon: PaymentIcon,
        // Opens page with settings_list; payments APIs use payments_list (§4.18 + §4.19)
        permissions: [
          { code: P.SETTINGS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.PAYMENTS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.list' },
          { code: P.SETTINGS_VIEW_DETAIL, actionKey: 'settingsPages.rolesPermsEditor.actions.paymentSettingsLoad' },
          { code: P.SETTINGS_UPDATE_DETAIL },
          { code: 'settings_manage' },
          { code: P.ACCOUNTS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.accountSelector' },
          { code: P.ACCOUNTS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.accountDisplay' },
        ],
      },
      {
        id: 'shipmentsConfig',
        labelKey: 'nav.shipmentsConfig',
        route: '/settings/shipments-config',
        icon: InventoryIcon,
        permissions: [
          { code: P.SETTINGS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SETTINGS_VIEW_DETAIL, actionKey: 'settingsPages.rolesPermsEditor.actions.viewDetail' },
          { code: P.SETTINGS_UPDATE_DETAIL },
          { code: 'settings_manage' },
        ],
      },
    ],
  },
  {
    id: 'settingsIntegrations',
    labelKey: 'nav.sectionIntegrations',
    icon: WhatsAppIcon,
    pages: [
      {
        id: 'twilioSettings',
        labelKey: 'nav.twilioSettings',
        route: '/settings/twilio',
        icon: PhoneIcon,
        permissions: [
          { code: P.SETTINGS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SETTINGS_VIEW_DETAIL, actionKey: 'settingsPages.rolesPermsEditor.actions.viewDetail' },
          { code: P.SETTINGS_UPDATE_DETAIL },
          { code: 'settings_manage' },
        ],
      },
      {
        id: 'whatsappMessages',
        labelKey: 'nav.whatsappMessages',
        route: '/settings/whatsapp-messages',
        icon: WhatsAppIcon,
        permissions: [
          { code: P.SETTINGS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SETTINGS_VIEW_DETAIL, actionKey: 'settingsPages.rolesPermsEditor.actions.viewDetail' },
          { code: P.SETTINGS_UPDATE_DETAIL },
          { code: 'settings_manage' },
        ],
      },
      {
        id: 'whatsappChatbot',
        labelKey: 'nav.whatsappChatBot',
        route: '/settings/whatsapp-chatbot',
        icon: SmartToyIcon,
        permissions: [
          { code: P.SETTINGS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SETTINGS_VIEW_DETAIL, actionKey: 'settingsPages.rolesPermsEditor.actions.viewDetail' },
          { code: P.SETTINGS_UPDATE_DETAIL },
          { code: 'settings_manage' },
        ],
      },
    ],
  },
  {
    id: 'settingsAccessControl',
    labelKey: 'nav.sectionAccessControl',
    icon: SecurityIcon,
    pages: [
      {
        id: 'permissions',
        labelKey: 'nav.permissions',
        route: '/settings/permissions',
        icon: SecurityIcon,
        permissions: [
          { code: P.PERMISSIONS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.PERMISSIONS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.PERMISSIONS_CREATE },
          { code: P.PERMISSIONS_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.PERMISSIONS_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
        ],
      },
      {
        id: 'roles',
        labelKey: 'nav.roles',
        route: '/settings/roles',
        icon: GroupIcon,
        permissions: [
          { code: P.ROLES_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.ROLES_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.ROLES_CREATE },
          { code: P.ROLES_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.ROLES_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
          { code: P.ROLES_VIEW_PERMISSIONS },
          { code: P.ROLES_ASSIGN_PERMISSIONS },
          { code: P.PERMISSIONS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.permissionPicker' },
        ],
      },
      {
        id: 'users',
        labelKey: 'nav.users',
        route: '/settings/users',
        icon: PersonIcon,
        permissions: [
          { code: P.USERS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.USERS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.USERS_CREATE },
          { code: P.USERS_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.USERS_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
        ],
      },
      {
        id: 'userRoles',
        labelKey: 'nav.userRoleAssignments',
        route: '/settings/user-roles',
        icon: AssignmentIcon,
        // §4.1 #23–24 — assign/revoke helpers have no UI consumer (Phase 7)
        permissions: [
          { code: P.USER_ROLES_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.USER_ROLES_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.USER_ROLES_CREATE },
          { code: P.USER_ROLES_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.USER_ROLES_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
          { code: P.ROLES_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.roleDropdown' },
        ],
      },
    ],
  },
  {
    id: 'master',
    labelKey: 'nav.sectionMasterData',
    icon: CategoryIcon,
    pages: [
      {
        id: 'trackingEventTypes',
        labelKey: 'nav.trackingEventTypes',
        route: '/settings/tracking-event-types',
        icon: ListAltIcon,
        permissions: [
          { code: P.SHIPMENT_TRACKING_EVENT_TYPES_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPMENT_TRACKING_EVENT_TYPES_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.SHIPMENT_TRACKING_EVENT_TYPES_CREATE },
          { code: P.SHIPMENT_TRACKING_EVENT_TYPES_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.SHIPMENT_TRACKING_EVENT_TYPES_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
        ],
      },
      {
        id: 'shippingCompanies',
        labelKey: 'nav.shippingCompanies',
        route: '/settings/shipping-companies',
        icon: LocalShippingIcon,
        permissions: [
          { code: P.SHIPPING_COMPANIES_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPPING_COMPANIES_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.SHIPPING_COMPANIES_CREATE },
          { code: P.SHIPPING_COMPANIES_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.SHIPPING_COMPANIES_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
        ],
      },
      {
        id: 'shippingWarehouses',
        labelKey: 'nav.shippingWarehouses',
        route: '/settings/shipping-warehouses',
        icon: WarehouseIcon,
        permissions: [
          { code: P.SHIPPING_WAREHOUSES_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPPING_WAREHOUSES_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.SHIPPING_WAREHOUSES_CREATE },
          { code: P.SHIPPING_WAREHOUSES_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.SHIPPING_WAREHOUSES_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
        ],
      },
      {
        id: 'shipmentTypes',
        labelKey: 'nav.shipmentTypes',
        route: '/settings/shipment-types',
        icon: InventoryIcon,
        permissions: [
          { code: P.SHIPMENT_TYPES_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SHIPMENT_TYPES_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.SHIPMENT_TYPES_CREATE },
          { code: P.SHIPMENT_TYPES_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.SHIPMENT_TYPES_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
        ],
      },
      {
        id: 'locations',
        labelKey: 'nav.locations',
        route: '/settings/locations',
        icon: LocationOnIcon,
        permissions: [
          { code: P.LOCATIONS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.LOCATIONS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.LOCATIONS_CREATE },
          { code: P.LOCATIONS_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.LOCATIONS_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
          { code: P.LOCATIONS_MANAGE, actionKey: 'settingsPages.rolesPermsEditor.actions.manageHierarchy' },
        ],
      },
      {
        id: 'accounts',
        labelKey: 'nav.accounts',
        route: '/settings/accounts',
        icon: AccountBalanceIcon,
        permissions: [
          { code: P.ACCOUNTS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.ACCOUNTS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.ACCOUNTS_CREATE },
          { code: P.ACCOUNTS_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.ACCOUNTS_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
        ],
      },
      {
        id: 'settlementGroupsList',
        labelKey: 'nav.settlementGroups',
        route: '/settings/settlementgroups',
        icon: AttachMoneyIcon,
        permissions: [
          { code: P.SETTLEMENT_GROUPS_LIST, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SETTLEMENT_GROUPS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.rowDetails' },
          { code: P.SETTLEMENT_GROUPS_CREATE },
          { code: P.SETTLEMENT_GROUPS_UPDATE, actionKey: 'settingsPages.rolesPermsEditor.actions.editRow' },
          { code: P.SETTLEMENT_GROUPS_DELETE, actionKey: 'settingsPages.rolesPermsEditor.actions.deleteRow' },
        ],
      },
      {
        id: 'addSettlementGroup',
        labelKey: 'settingsPages.rolesPermsEditor.addSettlementGroup',
        route: '/settings/settlementgroups/Add',
        icon: AddCircleIcon,
        permissions: [
          { code: P.SETTLEMENT_GROUPS_CREATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SETTLEMENT_GROUPS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.listNav' },
        ],
      },
      {
        id: 'editSettlementGroup',
        labelKey: 'settingsPages.rolesPermsEditor.editSettlementGroup',
        route: '/settings/settlementgroups/:id',
        icon: AddCircleIcon,
        permissions: [
          { code: P.SETTLEMENT_GROUPS_UPDATE, roleKey: 'settingsPages.rolesPermsEditor.routeAccess' },
          { code: P.SETTLEMENT_GROUPS_VIEW, actionKey: 'settingsPages.rolesPermsEditor.actions.loadSettlementGroup' },
          { code: P.SETTLEMENT_GROUPS_LIST, actionKey: 'settingsPages.rolesPermsEditor.actions.listNav' },
        ],
      },
    ],
  },
];

// Humanise a codename fallback when the backend doesn't have the permission
// registered yet. Turns "shipments_bulk_update" into "Shipments Bulk Update".
export const humanize = (codename = '') =>
  String(codename)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * i18n key for a permission codename (`settingsPages.rolesPermsEditor.permissionByCodename.*` in ui.json).
 */
export const permissionCodenameLabelKey = (codename = '') =>
  `settingsPages.rolesPermsEditor.permissionByCodename.${String(codename || '')}`;

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

function CountChip({ selected, total, tone = 'neutral' }) {
  const color =
    total === 0 ? 'neutral' : selected === total ? 'success' : selected > 0 ? 'primary' : tone;
  return (
    <Chip
      size="sm"
      variant="soft"
      color={color}
      sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 48, justifyContent: 'center' }}
    >
      {selected} / {total}
    </Chip>
  );
}

function PermissionRow({
  perm,
  permObject,
  checked,
  onToggle,
  t,
  alsoOnPages,
  currentPageId,
  highlight,
}) {
  const codename = (permObject && permObject.codename) || perm.code;
  const fallbackLabel = (permObject && permObject.name) || humanize(perm.code);
  const friendlyName = t(permissionCodenameLabelKey(codename), {
    defaultValue: fallbackLabel,
  });
  const missing = !permObject;
  const otherPages = alsoOnPages.filter((p) => p.id !== currentPageId);
  const hasDescription = Boolean(permObject && permObject.description);
  // What UI affordance does this permission gate on *this* page? Explicit
  // overrides on the tree entry win; otherwise we auto-derive from the
  // codename suffix, falling back to the generic "Opens page" chip for
  // route-level permissions.
  const actionKey =
    perm.actionKey || perm.roleKey || autoActionKey(perm.code);
  const isRoute = perm.roleKey === 'settingsPages.rolesPermsEditor.routeAccess';

  return (
    <Box
      onClick={() => !missing && onToggle(permObject.id)}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        p: 1,
        pl: 1.25,
        borderRadius: '8px',
        cursor: missing ? 'not-allowed' : 'pointer',
        border: '1px solid',
        borderColor: checked ? 'primary.300' : 'transparent',
        bgcolor: highlight
          ? 'warning.softBg'
          : checked
            ? 'primary.softBg'
            : 'transparent',
        opacity: missing ? 0.55 : 1,
        transition: 'background-color 120ms ease, border-color 120ms ease',
        '&:hover': {
          bgcolor: missing
            ? undefined
            : checked
              ? 'primary.softHoverBg'
              : 'background.level1',
        },
      }}
    >
      <Box sx={{ pt: '2px' }}>
        <NativeCheckbox
          size="sm"
          checked={checked}
          disabled={missing}
          onChange={() => !missing && onToggle(permObject.id)}
          onClick={(e) => e.stopPropagation()}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography
            level="body-sm"
            sx={{ fontWeight: 600, color: missing ? 'neutral.500' : 'text.primary' }}
          >
            {friendlyName}
          </Typography>
          {actionKey && (
            <Chip
              size="sm"
              variant={isRoute ? 'soft' : 'outlined'}
              color={isRoute ? 'primary' : 'neutral'}
              sx={{ fontSize: 10 }}
            >
              {t(actionKey)}
            </Chip>
          )}
          {otherPages.length > 0 && (
            <Tooltip
              variant="soft"
              color="warning"
              placement="top"
              title={
                <Box sx={{ maxWidth: 280 }}>
                  <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t('settingsPages.rolesPermsEditor.alsoOnTooltip')}
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    {otherPages.map((pg) => (
                      <li key={pg.id}>
                        <Typography level="body-xs">
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {pg.label}
                          </Box>
                          {pg.actionKey ? (
                            <Box component="span" sx={{ color: 'text.secondary' }}>
                              {' · '}{t(pg.actionKey)}
                            </Box>
                          ) : null}
                          {pg.groupLabel ? (
                            <Box component="span" sx={{ color: 'text.tertiary' }}>
                              {' '}({pg.groupLabel})
                            </Box>
                          ) : null}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              }
            >
              <Chip
                size="sm"
                variant="soft"
                color="warning"
                startDecorator={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
                sx={{ fontSize: 10 }}
                onClick={(e) => e.stopPropagation()}
              >
                {t('settingsPages.rolesPermsEditor.alsoOn', { count: otherPages.length })}
              </Chip>
            </Tooltip>
          )}
          {missing && (
            <Chip size="sm" variant="soft" color="danger" sx={{ fontSize: 10 }}>
              {t('settingsPages.rolesPermsEditor.missing')}
            </Chip>
          )}
        </Box>
        <Typography
          level="body-xs"
          sx={{
            mt: 0.25,
            fontFamily:
              'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
            color: 'text.tertiary',
          }}
        >
          {codename}
        </Typography>
        {hasDescription && (
          <Typography level="body-xs" sx={{ mt: 0.25, color: 'text.secondary' }}>
            {permObject.description}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function PageBlock({
  page,
  selectedIds,
  permByCode,
  codeToPages,
  onToggle,
  onBulkToggle,
  expanded,
  onToggleExpand,
  t,
  searchMatchedCodes,
  isSearching,
}) {
  const IconCmp = page.icon || ListAltIcon;
  const pageTitle = t(page.labelKey);

  // Resolve each perm's backend object
  const resolved = useMemo(
    () =>
      page.permissions
        .map((p) => ({
          def: p,
          obj: permByCode.get(p.code),
        })),
    [page.permissions, permByCode]
  );

  const selectableIds = useMemo(
    () => resolved.map((r) => (r.obj ? r.obj.id : null)).filter(Boolean),
    [resolved]
  );
  const selectedOnPage = useMemo(
    () => selectableIds.filter((id) => selectedIds.has(id)).length,
    [selectableIds, selectedIds]
  );
  const total = selectableIds.length;
  const allSelected = total > 0 && selectedOnPage === total;

  return (
    <Sheet
      variant="outlined"
      sx={{
        borderRadius: '10px',
        p: 1.25,
        bgcolor: 'background.surface',
        borderColor: selectedOnPage > 0 ? 'primary.200' : 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '8px',
            bgcolor: 'primary.softBg',
            color: 'primary.solidBg',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <IconCmp sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography level="title-sm" sx={{ lineHeight: 1.25 }}>
            {pageTitle}
          </Typography>
          {page.route && (
            <Typography
              level="body-xs"
              sx={{
                color: 'text.tertiary',
                fontFamily:
                  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
              }}
            >
              {page.route}
            </Typography>
          )}
        </Box>
        <CountChip selected={selectedOnPage} total={total} />
        <Tooltip
          title={
            allSelected
              ? t('settingsPages.rolesPermsEditor.revokeAll')
              : t('settingsPages.rolesPermsEditor.grantAll')
          }
        >
          <span>
            <Button
              size="sm"
              variant={allSelected ? 'soft' : 'plain'}
              color={allSelected ? 'danger' : 'primary'}
              onClick={(e) => {
                e.stopPropagation();
                onBulkToggle(selectableIds, !allSelected);
              }}
              disabled={total === 0}
              sx={{ minHeight: 28, px: 1 }}
            >
              {allSelected
                ? t('settingsPages.rolesPermsEditor.revokeAll')
                : t('settingsPages.rolesPermsEditor.grantAll')}
            </Button>
          </span>
        </Tooltip>
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          onClick={onToggleExpand}
          disabled={total === 0}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: '1px dashed',
            borderColor: 'divider',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 0.75,
          }}
        >
          {resolved.length === 0 && (
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              {t('settingsPages.rolesPermsEditor.noPermsOnPage')}
            </Typography>
          )}
          {resolved.map(({ def, obj }) => {
            const pages = codeToPages.get(def.code) || [];
            const checked = obj ? selectedIds.has(obj.id) : false;
            const highlight = isSearching && searchMatchedCodes.has(def.code);
            return (
              <PermissionRow
                key={`${page.id}-${def.code}`}
                perm={def}
                permObject={obj}
                checked={checked}
                onToggle={onToggle}
                t={t}
                alsoOnPages={pages}
                currentPageId={page.id}
                highlight={highlight}
              />
            );
          })}
        </Box>
      </Collapse>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function RolePermissionsByPageEditor({
  allPermissions,
  selectedPermissionIds,
  onChange,
}) {
  const { t } = useTranslation('ui');
  const [search, setSearch] = useState('');

  const tree = useMemo(() => buildNavigationTree(), []);

  // Start with every page (and thus all permissions) collapsed so the modal
  // opens compact — the admin can dive into the page(s) they care about.
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [collapsedPages, setCollapsedPages] = useState(() => {
    const s = new Set();
    tree.forEach((g) => g.pages.forEach((p) => s.add(p.id)));
    return s;
  });

  // Backend permission indexes
  const permByCode = useMemo(() => {
    const m = new Map();
    (allPermissions || []).forEach((p) => {
      if (p && p.codename) m.set(p.codename, p);
      // Some backends use `name` as codename.
      if (p && p.name && !m.has(p.name)) m.set(p.name, p);
    });
    return m;
  }, [allPermissions]);

  // Reverse index: codename → list of pages where it appears (with the
  // per-occurrence action label so the "Also on" tooltip can tell the admin
  // exactly which affordance the permission unlocks elsewhere).
  const codeToPages = useMemo(() => {
    const m = new Map();
    tree.forEach((group) => {
      const groupLabel = t(group.labelKey);
      group.pages.forEach((page) => {
        const pageLabel = t(page.labelKey);
        page.permissions.forEach((p) => {
          const entry = {
            id: page.id,
            label: pageLabel,
            groupLabel,
            actionKey: p.actionKey || p.roleKey || autoActionKey(p.code),
          };
          if (!m.has(p.code)) m.set(p.code, []);
          m.get(p.code).push(entry);
        });
      });
    });
    return m;
  }, [tree, t]);

  const selectedSet = useMemo(
    () => new Set(selectedPermissionIds || []),
    [selectedPermissionIds]
  );

  // Search filter
  const search_ = search.trim().toLowerCase();
  const isSearching = search_.length > 0;

  const searchMatchedCodes = useMemo(() => {
    if (!isSearching) return new Set();
    const matched = new Set();
    (allPermissions || []).forEach((p) => {
      const hay = `${p.name || ''} ${p.codename || ''} ${p.description || ''} ${p.app_label || ''}`.toLowerCase();
      if (hay.includes(search_)) {
        if (p.codename) matched.add(p.codename);
        if (p.name) matched.add(p.name);
      }
    });
    return matched;
  }, [allPermissions, search_, isSearching]);

  // Which pages should be shown after search filtering
  const visibleTree = useMemo(() => {
    if (!isSearching) return tree;
    return tree
      .map((group) => {
        const pages = group.pages.filter((page) => {
          const pageLabel = t(page.labelKey).toLowerCase();
          const groupLabel = t(group.labelKey).toLowerCase();
          if (pageLabel.includes(search_) || groupLabel.includes(search_)) return true;
          return page.permissions.some((p) => {
            if (searchMatchedCodes.has(p.code)) return true;
            return p.code.toLowerCase().includes(search_);
          });
        });
        return { ...group, pages };
      })
      .filter((g) => g.pages.length > 0);
  }, [tree, isSearching, search_, searchMatchedCodes, t]);

  // --- Toggle helpers ---
  const handleToggle = useCallback(
    (id) => {
      const next = new Set(selectedSet);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onChange(Array.from(next));
    },
    [selectedSet, onChange]
  );

  const handleBulkToggle = useCallback(
    (ids, select) => {
      if (!ids || ids.length === 0) return;
      const next = new Set(selectedSet);
      ids.forEach((id) => {
        if (select) next.add(id);
        else next.delete(id);
      });
      onChange(Array.from(next));
    },
    [selectedSet, onChange]
  );

  const toggleGroup = useCallback((groupId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const togglePage = useCallback((pageId) => {
    setCollapsedPages((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    setCollapsedGroups(new Set());
    setCollapsedPages(new Set());
  }, []);

  const handleCollapseAll = useCallback(() => {
    const allPageIds = new Set();
    const allGroupIds = new Set();
    tree.forEach((g) => {
      allGroupIds.add(g.id);
      g.pages.forEach((p) => allPageIds.add(p.id));
    });
    setCollapsedGroups(allGroupIds);
    setCollapsedPages(allPageIds);
  }, [tree]);

  // --- Stats ---
  const allKnownCodes = useMemo(() => {
    const set = new Set();
    tree.forEach((g) => g.pages.forEach((pg) => pg.permissions.forEach((p) => set.add(p.code))));
    return set;
  }, [tree]);

  // Permissions that exist in backend but aren't mapped to any navigation page.
  const orphanPermissions = useMemo(() => {
    return (allPermissions || []).filter((p) => {
      const code = p.codename || p.name;
      return code && !allKnownCodes.has(code);
    });
  }, [allPermissions, allKnownCodes]);

  const totalBackendPerms = (allPermissions || []).length;
  const selectedCount = selectedSet.size;

  const mapGroupSelected = (group) => {
    let selected = 0;
    let total = 0;
    group.pages.forEach((page) => {
      page.permissions.forEach((p) => {
        const obj = permByCode.get(p.code);
        if (!obj) return;
        total += 1;
        if (selectedSet.has(obj.id)) selected += 1;
      });
    });
    return { selected, total };
  };

  return (
    <Box>
      {/* Toolbar */}
      <Sheet
        variant="outlined"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          borderRadius: '10px',
          p: 1.25,
          mb: 1.5,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'background.body',
        }}
      >
        <Input
          size="sm"
          placeholder={t('settingsPages.rolesPermsEditor.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          startDecorator={<SearchIcon fontSize="small" />}
          sx={{ flex: 1, minWidth: 220 }}
        />
        <Chip
          size="md"
          variant="soft"
          color={selectedCount > 0 ? 'primary' : 'neutral'}
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {t('settingsPages.rolesPermsEditor.summary', {
            selected: selectedCount,
            total: totalBackendPerms,
          })}
        </Chip>
        <Button
          size="sm"
          variant="plain"
          color="neutral"
          onClick={handleExpandAll}
          startDecorator={<ExpandMoreIcon />}
        >
          {t('settingsPages.rolesPermsEditor.expandAll')}
        </Button>
        <Button
          size="sm"
          variant="plain"
          color="neutral"
          onClick={handleCollapseAll}
          startDecorator={<ExpandLessIcon />}
        >
          {t('settingsPages.rolesPermsEditor.collapseAll')}
        </Button>
      </Sheet>

      {/* Groups */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {visibleTree.length === 0 && (
          <Sheet
            variant="soft"
            color="neutral"
            sx={{ p: 2, borderRadius: '10px', textAlign: 'center' }}
          >
            <Typography level="body-sm">{t('settingsPages.rolesPermsEditor.noResults')}</Typography>
          </Sheet>
        )}
        {visibleTree.map((group) => {
          const GroupIconCmp = group.icon || CategoryIcon;
          const groupStats = mapGroupSelected(group);
          const groupCollapsed = collapsedGroups.has(group.id) && !isSearching;
          const groupLabel = t(group.labelKey);

          const allGroupIds = [];
          group.pages.forEach((page) =>
            page.permissions.forEach((p) => {
              const obj = permByCode.get(p.code);
              if (obj) allGroupIds.push(obj.id);
            })
          );
          const dedupedGroupIds = Array.from(new Set(allGroupIds));
          const allGroupSelected =
            dedupedGroupIds.length > 0 &&
            dedupedGroupIds.every((id) => selectedSet.has(id));

          return (
            <Sheet
              key={group.id}
              variant="outlined"
              sx={{
                borderRadius: '12px',
                p: 1.25,
                bgcolor: 'background.level1',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                onClick={() => toggleGroup(group.id)}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '10px',
                    bgcolor: 'background.surface',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <GroupIconCmp sx={{ fontSize: 20, color: 'primary.solidBg' }} />
                </Box>
                <Typography level="title-md" sx={{ flex: 1 }}>
                  {groupLabel}
                </Typography>
                <CountChip selected={groupStats.selected} total={groupStats.total} />
                <Button
                  size="sm"
                  variant={allGroupSelected ? 'soft' : 'outlined'}
                  color={allGroupSelected ? 'danger' : 'primary'}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBulkToggle(dedupedGroupIds, !allGroupSelected);
                  }}
                  disabled={dedupedGroupIds.length === 0}
                  sx={{ minHeight: 28 }}
                >
                  {allGroupSelected
                    ? t('settingsPages.rolesPermsEditor.revokeAll')
                    : t('settingsPages.rolesPermsEditor.grantAll')}
                </Button>
                <IconButton size="sm" variant="plain" color="neutral">
                  {groupCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </IconButton>
              </Box>

              <Collapse in={!groupCollapsed} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  {group.pages.map((page) => {
                    const pageExpanded = !collapsedPages.has(page.id) || isSearching;
                    return (
                      <PageBlock
                        key={page.id}
                        page={page}
                        selectedIds={selectedSet}
                        permByCode={permByCode}
                        codeToPages={codeToPages}
                        onToggle={handleToggle}
                        onBulkToggle={handleBulkToggle}
                        expanded={pageExpanded}
                        onToggleExpand={() => togglePage(page.id)}
                        t={t}
                        searchMatchedCodes={searchMatchedCodes}
                        isSearching={isSearching}
                      />
                    );
                  })}
                </Box>
              </Collapse>
            </Sheet>
          );
        })}

        {/* Orphan permissions (registered on backend but not on any page) */}
        {orphanPermissions.length > 0 && (
          <Sheet
            variant="outlined"
            sx={{
              borderRadius: '12px',
              p: 1.25,
              bgcolor: 'background.level1',
              borderStyle: 'dashed',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <InfoOutlinedIcon sx={{ color: 'warning.solidBg' }} />
              <Box sx={{ flex: 1 }}>
                <Typography level="title-sm">
                  {t('settingsPages.rolesPermsEditor.otherPermissions')}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  {t('settingsPages.rolesPermsEditor.otherPermissionsHint')}
                </Typography>
              </Box>
              <CountChip
                selected={
                  orphanPermissions.filter((p) => selectedSet.has(p.id)).length
                }
                total={orphanPermissions.length}
              />
              <Button
                size="sm"
                variant="outlined"
                color="primary"
                onClick={() =>
                  handleBulkToggle(
                    orphanPermissions.map((p) => p.id),
                    !orphanPermissions.every((p) => selectedSet.has(p.id))
                  )
                }
              >
                {orphanPermissions.every((p) => selectedSet.has(p.id))
                  ? t('settingsPages.rolesPermsEditor.revokeAll')
                  : t('settingsPages.rolesPermsEditor.grantAll')}
              </Button>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 0.75,
              }}
            >
              {orphanPermissions.map((p) => (
                <PermissionRow
                  key={`orphan-${p.id}`}
                  perm={{ code: p.codename || p.name }}
                  permObject={p}
                  checked={selectedSet.has(p.id)}
                  onToggle={handleToggle}
                  t={t}
                  alsoOnPages={[]}
                  currentPageId="__orphan__"
                  highlight={
                    isSearching && searchMatchedCodes.has(p.codename || p.name)
                  }
                />
              ))}
            </Box>
          </Sheet>
        )}
      </Box>
    </Box>
  );
}
